import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";

/**
 * Sticky sequential reveal. Given an ordered array of "answered" booleans,
 * returns how many questions should be visible: all consecutively-answered
 * leading questions plus the next unanswered one. The count only ever moves
 * forward, so editing an earlier answer never hides later questions.
 */
export function useSequentialReveal(flags, { enabled = true } = {}) {
  const key = flags.map((f) => (f ? "1" : "0")).join("");
  const [visible, setVisible] = useState(enabled ? 1 : flags.length);
  useEffect(() => {
    if (!enabled) {
      setVisible(flags.length);
      return;
    }
    let consec = 0;
    while (consec < flags.length && flags[consec]) consec++;
    const target = Math.min(flags.length, consec + 1);
    setVisible((v) => (target > v ? target : v));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, flags.length, enabled]);
  return visible;
}

/**
 * Smoothly bring a freshly-revealed section into view, but only when it is not
 * already comfortably visible (so entering a stage never yanks the first
 * question around). Users keep full control to scroll back up at any time.
 */
let _subsetScrollUntil = 0;

export function scrollRevealIntoView(el) {
  if (!el || typeof window === "undefined") return;
  if (Date.now() < _subsetScrollUntil) return; // a sub-set is taking scroll priority
  const r = el.getBoundingClientRect();
  // Bring the newly-revealed question TITLE just below the sticky header, rather than
  // centring it (which can hide the title and show only the options). Skip if it is
  // already sitting comfortably near the top.
  if (r.top > 150 || r.top < 90) {
    const top = r.top + window.scrollY - 110;
    window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
  }
}

/**
 * Rendered inside a conditional sub-set (follow-up questions revealed by an answer).
 * On mount it briefly suppresses the competing "next question" auto-scroll so the user
 * stays on the sub-set instead of being skipped past it.
 */
export const ScrollLock = () => {
  useEffect(() => {
    _subsetScrollUntil = Date.now() + 700;
    return () => { _subsetScrollUntil = 0; };
  }, []);
  return null;
};

/** Briefly suppress the next-question auto-scroll (used by multi-select questions so a
 * single tick doesn't scroll the user away before they finish choosing). */
export function lockRevealScroll(ms = 1200) {
  _subsetScrollUntil = Date.now() + ms;
}

let _lastTypeAt = 0;
let _pendingTimer = null;

/** Called on every keystroke in a free-text field so reveal-scroll waits for a quiet gap. */
export function markTyping() {
  _lastTypeAt = Date.now();
}

export function cancelPendingRevealScroll() {
  if (_pendingTimer) { clearTimeout(_pendingTimer); _pendingTimer = null; }
}

/**
 * Scroll a newly-revealed section into view. If the user has been typing, wait until
 * ~3s of quiet has passed (rescheduling while they keep typing). Cancels if the user
 * scrolls manually in the meantime.
 */
export function scheduleRevealScroll(el, { quiet = 3000 } = {}) {
  if (!el || typeof window === "undefined") return;
  cancelPendingRevealScroll();
  if (Date.now() - _lastTypeAt >= quiet) {
    scrollRevealIntoView(el);
    return;
  }
  const onUserScroll = () => {
    cancelPendingRevealScroll();
    window.removeEventListener("wheel", onUserScroll);
    window.removeEventListener("touchmove", onUserScroll);
  };
  window.addEventListener("wheel", onUserScroll, { passive: true });
  window.addEventListener("touchmove", onUserScroll, { passive: true });
  const tick = () => {
    const since = Date.now() - _lastTypeAt;
    if (since < quiet) {
      _pendingTimer = setTimeout(tick, quiet - since + 20);
    } else {
      _pendingTimer = null;
      window.removeEventListener("wheel", onUserScroll);
      window.removeEventListener("touchmove", onUserScroll);
      scrollRevealIntoView(el);
    }
  };
  _pendingTimer = setTimeout(tick, quiet - (Date.now() - _lastTypeAt) + 20);
}

export const Reveal = ({ show, children, scroll = true }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!show || !scroll) return undefined;
    const id = setTimeout(() => scheduleRevealScroll(ref.current), 130);
    return () => clearTimeout(id);
  }, [show, scroll]);
  if (!show) return null;
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};
